import React, { forwardRef } from 'react';
import { FONT } from '@/lib/bracket/text';
import { MatchCardSvg } from './MatchCardSvg';
import { LineSvg, HeaderSvg, NoteSvg, ChampionSvg, TitleBand, FooterBand, WatermarkSvg } from './SheetParts';

// One printable page as a single vector SVG (1 unit = 1 pt).
export const BracketSheet = forwardRef(({
  layout, index, width, height, title, subtitle, eventLogo, logoUrl, onSelect, blank, showCourt, uid, noIds, nameFs,
  writeIn, champNames, onSlot, watermark, canEdit, statsLinks,
}, ref) => {
  const { page, area, geom, scale, M } = layout;
  const vp = geom.viewports[index];
  const items = vp.groups ? geom.items.filter((it) => vp.groups.includes(it.pg || 'C')) : geom.items;
  const ox = area.x + (area.w - vp.w * scale) / 2 - vp.x * scale;
  // Short brackets sit just under the title instead of floating mid-page.
  const freeY = area.h - vp.h * scale;
  const oy = area.y + Math.min(freeY / 2, 28) - vp.y * scale;
  const clipBase = `${uid}${noIds ? 'p' : 's'}${index}`;
  const pageLabel = layout.pageCount > 1 ? `PAGE ${index + 1} OF ${layout.pageCount}` : null;
  const hasDashed = geom.items.some((it) => it.dashed);
  return (
    <svg x-excluded="true" ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${page.w} ${page.h}`} width={width} height={height}
      fontFamily={FONT} data-testid={noIds ? undefined : `bracket-sheet-${index}`} style={{ display: 'block' }}>
      <rect width={page.w} height={page.h} fill="#FFFFFF" />
      <TitleBand layout={layout} title={title} subtitle={subtitle} eventLogo={eventLogo} pageLabel={pageLabel} noIds={noIds || index > 0} />
      <g x-excluded="true" transform={`translate(${ox.toFixed(2)} ${oy.toFixed(2)}) scale(${scale.toFixed(4)})`}>
        {items.filter((it) => it.type === 'line').map((it, i) => <LineSvg key={`l${i}`} item={it} M={M} />)}
        {items.filter((it) => it.type === 'header').map((it, i) => <HeaderSvg key={`h${i}`} item={noIds ? { ...it, testId: undefined } : it} M={M} />)}
        {items.filter((it) => it.type === 'note').map((it, i) => <NoteSvg key={`n${i}`} item={it} />)}
        {items.filter((it) => it.type === 'champion').map((it, i) => (
          <ChampionSvg key={`c${i}`} item={it} M={M} noIds={noIds || index > 0} blank={blank} writeIn={writeIn} champNames={champNames} onSlot={onSlot} />
        ))}
        {items.filter((it) => it.type === 'match').map((it) => (
          <MatchCardSvg key={`${it.id}-${it.pg || 'C'}`} card={it} M={M} logoUrl={logoUrl} onSelect={onSelect} blank={blank}
            showCourt={showCourt} nameFs={nameFs} clipBase={clipBase} noIds={noIds || (index > 0 && it.pg === 'C')} writeIn={writeIn} onSlot={onSlot}
            canEdit={canEdit} statsHref={statsLinks?.[it.match.id]} />
        ))}
      </g>
      <FooterBand layout={layout} hasDashed={hasDashed} />
      {watermark && <WatermarkSvg page={page} text={typeof watermark === 'string' ? watermark : undefined} />}
    </svg>
  );
});

BracketSheet.displayName = 'BracketSheet';
